import { Box, Button } from "@mui/material";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { useCallback, useEffect, useRef, useState } from "react";

import { theme } from "../../theme";
import { useTranslation } from "react-i18next";

const ChatScrollContainer = (props: { children: React.ReactNode }) => {
  const { t } = useTranslation("ChatUx");

  // Refs
  const outerDiv = useRef<HTMLDivElement | null>(null);
  const innerDiv = useRef<HTMLDivElement | null>(null);
  const prevInnerDivHeight = useRef(0);

  // Component state
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isScrollingToBottom, setIsScrollingToBottom] = useState(false);
  const [scrollingTimeoutId, setScrollingTimeoutId] = useState<
    number | undefined
  >(undefined);

  // Reactive code
  useEffect(() => {
    // Auto-scroll to the bottom of the chat when new messages are added
    const outerDivHeight = Math.floor(outerDiv.current!.clientHeight);
    const innerDivHeight = Math.floor(innerDiv.current!.clientHeight);
    const outerDivScrollTop = Math.floor(outerDiv.current!.scrollTop);

    if (prevInnerDivHeight.current !== innerDivHeight) {
      if (
        !prevInnerDivHeight.current ||
        Math.abs(
          prevInnerDivHeight.current - outerDivHeight - outerDivScrollTop
        ) < 5 || // "close enough" for being scrolled to the bottom
        isScrollingToBottom // free pass to scroll to the bottom since we're in the process already
      ) {
        setIsScrollingToBottom(true);
        outerDiv.current!.scrollTo({
          top: innerDivHeight - outerDivHeight,
          left: 0,
          behavior: prevInnerDivHeight.current ? "smooth" : "auto",
        });
        if (typeof scrollingTimeoutId !== "undefined") {
          window.clearTimeout(scrollingTimeoutId);
        }
        // Allow subsequent auto-scrolling to the bottom to be allowed within the next second.
        // This is so auto-scrolling isn't prevented for new content while it's already in the
        // process of auto-scrolling from previous content. i.e. If two messages are generated
        // in quick succession, we still want to auto-scroll for the second message while the
        // first is in-progress.
        setScrollingTimeoutId(
          window.setTimeout(() => {
            setIsScrollingToBottom(false);
            setScrollingTimeoutId(undefined);
          }, 1000)
        );
      } else {
        //TODO: Re-enable this once we can get the logic right
        //setShowScrollButton(true);
      }
      prevInnerDivHeight.current = innerDivHeight;
    }
  }, [props.children, isScrollingToBottom, scrollingTimeoutId]);

  // Callbacks
  const handleScrollButtonClick = useCallback(() => {
    // Scroll to the bottom when the scroll button is clicked
    const outerDivHeight = outerDiv.current!.clientHeight;
    const innerDivHeight = innerDiv.current!.clientHeight;
    outerDiv.current!.scrollTo({
      top: innerDivHeight - outerDivHeight,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const handleOnScroll = useCallback(() => {
    // Hide the scroll button when the user scrolls to the bottom
    const innerDivBottom =
      Math.floor(innerDiv.current!.scrollTop) + innerDiv.current!.clientHeight;
    const outerDivBottom =
      Math.floor(outerDiv.current!.scrollTop) + outerDiv.current!.clientHeight;
    if (innerDivBottom <= outerDivBottom) {
      setShowScrollButton(false);
    }
  }, []);

  return (
    <Box sx={styles.container}>
      <Box ref={outerDiv} sx={styles.outerDiv} onScroll={handleOnScroll}>
        <Box ref={innerDiv}>{props.children}</Box>
      </Box>
      {showScrollButton ? (
        <Button
          size="small"
          variant="outlined"
          sx={styles.scrollButton}
          onClick={handleScrollButtonClick}
        >
          {t("newMessage")}
        </Button>
      ) : null}
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    ...globalStyles.flexSpace,
    mx: -3,
    [theme.breakpoints.down("desktop")]: {
      mx: -2,
    },
    backgroundColor: "white",
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },
  outerDiv: {
    height: "100%",
    overflowY: "scroll",
  },
  scrollButton: {
    px: 2,
    backgroundColor: theme.palette.common.white,
    bottom: theme.spacing(2),
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
});

export default ChatScrollContainer;
